import { createRootRoute, Outlet } from '@tanstack/react-router';
import { t } from 'i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import React, { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { IndicatorTailwind } from 'src/components/indicators/IndicatorTailwind';
import { Alert, AlertDescription, AlertTitle } from 'src/components/ui/alert';
import { Button } from 'src/components/ui/button';
import { Toaster } from 'src/components/ui/sonner';
import { dpConfig } from 'src/config';
import { DefaultGuard } from 'src/guards/DefaultGuard';
import { BrandSidebarWithHeader } from 'src/layouts/BrandSidebarWithHeader';
import { ErrorsPage } from 'src/pages/errors/ErrorsPage';
import { NotFoundPage } from 'src/pages/errors/NotFoundPage';

const TanStackRouterDevtools = ['production', 'staging'].includes(
  `${dpConfig.env}`,
)
  ? () => null
  : lazy(() =>
      import('@tanstack/react-router-devtools').then((res) => ({
        default: res.TanStackRouterDevtools,
      })),
    );

const ReactQueryDevtools = ['production', 'staging'].includes(`${dpConfig.env}`)
  ? () => null
  : lazy(() =>
      import('@tanstack/react-query-devtools').then((res) => ({
        default: res.ReactQueryDevtools,
      })),
    );

export const RootRoute = createRootRoute({
  component: () => (
    <DefaultGuard>
      <ErrorBoundary
        fallbackRender={() => {
          return (
            <BrandSidebarWithHeader>
              <div className='flex h-full w-full items-center justify-center p-4'>
                <Alert variant='destructive' className='w-96'>
                  <AlertCircle className='h-4 w-4' />
                  <AlertTitle>
                    {t('An error occurred', { ns: 'errors' })}
                  </AlertTitle>
                  <AlertDescription className='flex items-center justify-between'>
                    <span>{t('Please reload the page', { ns: 'errors' })}</span>
                  </AlertDescription>
                  <div className='mt-3'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => window.location.reload()}
                      className='h-8 bg-transparent text-error-default'
                    >
                      <RefreshCw className='mr-2 h-3 w-3' />
                      {t('Reload', { ns: 'errors' })}
                    </Button>
                  </div>
                </Alert>
              </div>
            </BrandSidebarWithHeader>
          );
        }}
      >
        <Outlet />
      </ErrorBoundary>
      <Toaster closeButton richColors />
      <IndicatorTailwind />
      <Suspense>
        <TanStackRouterDevtools position='bottom-right' />
        <ReactQueryDevtools position='bottom' />
      </Suspense>
    </DefaultGuard>
  ),
  notFoundComponent: () => (
    <ErrorsPage>
      <NotFoundPage />
    </ErrorsPage>
  ),
});
